import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  verifyPasswordResetCode,
  confirmPasswordReset,
  applyActionCode,
} from 'firebase/auth';
import { auth } from '../firebase';
import theme from '../theme';

function getErrorMessage(error) {
  switch (error?.code) {
    case 'auth/expired-action-code':
      return 'This link has expired. Please request a new one from the app.';
    case 'auth/invalid-action-code':
      return 'This link is invalid or has already been used.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact support.';
    case 'auth/user-not-found':
      return 'No account found. It may have been deleted.';
    default:
      return 'Something went wrong. Please try again or contact support.';
  }
}

const successMessages = {
  resetPassword: {
    title: 'Password Updated!',
    body: 'Your password has been changed successfully. You can now sign in to WODGoat with your new password.',
  },
  verifyEmail: {
    title: 'Email Verified!',
    body: "Your email address has been verified. You're all set to use WODGoat.",
  },
  recoverEmail: {
    title: 'Email Address Restored',
    body: 'Your original email address has been recovered. You can now sign in with it.',
  },
};

function validatePassword(password) {
  if (!password.trim()) return 'Password is required';
  if (password.length < 8) return 'Password must be at least 8 characters long';
  if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
  if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter';
  if (!/[0-9]/.test(password)) return 'Password must contain at least one number';
  return '';
}

const AuthAction = () => {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');

  const [pageStatus, setPageStatus] = useState('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationError, setValidationError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!oobCode || !mode) {
      setErrorMessage('Invalid or malformed link. Please use the link from your email.');
      setPageStatus('error');
      return;
    }

    if (mode === 'resetPassword') {
      verifyPasswordResetCode(auth, oobCode)
        .then(() => setPageStatus('ready'))
        .catch((err) => {
          setErrorMessage(getErrorMessage(err));
          setPageStatus('error');
        });
    } else if (mode === 'verifyEmail' || mode === 'recoverEmail') {
      applyActionCode(auth, oobCode)
        .then(() => setPageStatus('success'))
        .catch((err) => {
          setErrorMessage(getErrorMessage(err));
          setPageStatus('error');
        });
    } else {
      setErrorMessage('This link type is not supported.');
      setPageStatus('error');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationError('');

    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setValidationError(passwordError);
      return;
    }
    if (newPassword !== confirmPassword) {
      setValidationError('Passwords do not match.');
      return;
    }

    setPageStatus('submitting');
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      setPageStatus('success');
    } catch (err) {
      setErrorMessage(getErrorMessage(err));
      setPageStatus('error');
    }
  };

  const styles = {
    page: {
      minHeight: 'calc(100vh - 200px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: `${theme.spacing.xl} ${theme.spacing.md}`,
    },
    card: {
      width: '100%',
      maxWidth: '480px',
      backgroundColor: theme.colors.backgroundGray,
      border: `1px solid ${theme.colors.border}`,
      borderRadius: theme.borderRadius.lg,
      padding: theme.spacing.xl,
      boxShadow: theme.shadows.lg,
      overflow: 'hidden',
    },
    title: {
      fontFamily: theme.typography.fontFamily.heading,
      fontSize: theme.typography.fontSize['2xl'],
      fontWeight: theme.typography.fontWeight.bold,
      color: theme.colors.text,
      textAlign: 'center',
      marginBottom: theme.spacing.xs,
    },
    subtitle: {
      fontFamily: theme.typography.fontFamily.primary,
      fontSize: theme.typography.fontSize.sm,
      color: theme.colors.textSecondary,
      textAlign: 'center',
      marginBottom: theme.spacing.lg,
      lineHeight: theme.typography.lineHeight.relaxed,
    },
    spinnerWrapper: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: theme.spacing.md,
      padding: `${theme.spacing.lg} 0`,
    },
    loadingText: {
      fontFamily: theme.typography.fontFamily.primary,
      fontSize: theme.typography.fontSize.sm,
      color: theme.colors.textSecondary,
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing.md,
    },
    label: {
      display: 'block',
      fontFamily: theme.typography.fontFamily.primary,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.medium,
      color: theme.colors.textLight,
      marginBottom: theme.spacing.xs,
    },
    inputWrapper: {
      position: 'relative',
    },
    input: {
      width: '100%',
      backgroundColor: theme.colors.background,
      border: `1px solid ${theme.colors.border}`,
      borderRadius: theme.borderRadius.base,
      padding: `14px ${theme.spacing.sm}`,
      paddingRight: '4.5rem',
      fontFamily: theme.typography.fontFamily.primary,
      fontSize: '16px', // explicit 16px prevents iOS auto-zoom on focus
      color: theme.colors.text,
      outline: 'none',
      boxSizing: 'border-box',
      minHeight: '52px',
    },
    toggleBtn: {
      position: 'absolute',
      right: theme.spacing.sm,
      top: '50%',
      transform: 'translateY(-50%)',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: theme.colors.textSecondary,
      fontFamily: theme.typography.fontFamily.primary,
      fontSize: theme.typography.fontSize.xs,
      fontWeight: theme.typography.fontWeight.semibold,
      padding: '8px 4px',
      letterSpacing: '0.05em',
      minWidth: '44px',
      minHeight: '44px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    validationError: {
      fontFamily: theme.typography.fontFamily.primary,
      fontSize: theme.typography.fontSize.sm,
      color: theme.colors.error,
      marginTop: `-${theme.spacing.xs}`,
    },
    submitBtn: {
      width: '100%',
      padding: `${theme.spacing.sm} ${theme.spacing.md}`,
      minHeight: '52px',
      backgroundColor: theme.colors.primary,
      color: theme.colors.white,
      border: 'none',
      borderRadius: theme.borderRadius.base,
      fontFamily: theme.typography.fontFamily.primary,
      fontSize: theme.typography.fontSize.base,
      fontWeight: theme.typography.fontWeight.semibold,
      cursor: 'pointer',
      marginTop: theme.spacing.xs,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing.xs,
      letterSpacing: '0.02em',
    },
    submitBtnDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
    statusIcon: {
      fontSize: '3.5rem',
      textAlign: 'center',
      display: 'block',
      marginBottom: theme.spacing.md,
    },
    statusTitle: {
      fontFamily: theme.typography.fontFamily.heading,
      fontSize: theme.typography.fontSize.xl,
      fontWeight: theme.typography.fontWeight.bold,
      color: theme.colors.text,
      textAlign: 'center',
      marginBottom: theme.spacing.xs,
    },
    statusBody: {
      fontFamily: theme.typography.fontFamily.primary,
      fontSize: theme.typography.fontSize.sm,
      color: theme.colors.textSecondary,
      textAlign: 'center',
      lineHeight: theme.typography.lineHeight.relaxed,
    },
    divider: {
      height: '1px',
      backgroundColor: theme.colors.border,
      margin: `${theme.spacing.md} 0`,
    },
    hint: {
      fontFamily: theme.typography.fontFamily.primary,
      fontSize: theme.typography.fontSize.xs,
      color: theme.colors.textSecondary,
      textAlign: 'center',
    },
  };

  const isSubmitting = pageStatus === 'submitting';

  return (
    <div className="wg-page" style={styles.page}>
      <style>{`
        @keyframes wg-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        .wg-spinner {
          width: 44px;
          height: 44px;
          border: 3px solid ${theme.colors.border};
          border-top-color: ${theme.colors.primary};
          border-radius: 50%;
          animation: wg-spin 0.75s linear infinite;
        }
        .wg-btn-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255,255,255,0.4);
          border-top-color: #fff;
          border-radius: 50%;
          animation: wg-spin 0.75s linear infinite;
          flex-shrink: 0;
        }
        .wg-input:focus {
          border-color: ${theme.colors.primary} !important;
          box-shadow: 0 0 0 2px ${theme.colors.primary}33;
        }
        @media (max-width: 480px) {
          .wg-page {
            align-items: flex-start !important;
            padding: 1.5rem 1rem !important;
          }
          .wg-card {
            padding: 1.5rem 1.25rem !important;
            border-radius: 0.75rem !important;
            box-shadow: none !important;
          }
          .wg-title {
            font-size: 1.375rem !important;
          }
          .wg-status-icon {
            font-size: 2.75rem !important;
          }
        }
      `}</style>

      <div className="wg-card" style={styles.card}>
        {/* Loading */}
        {pageStatus === 'loading' && (
          <div style={styles.spinnerWrapper}>
            <div className="wg-spinner" />
            <span style={styles.loadingText}>Verifying your link…</span>
          </div>
        )}

        {/* Password reset form */}
        {(pageStatus === 'ready' || pageStatus === 'submitting') && mode === 'resetPassword' && (
          <>
            <h1 className="wg-title" style={styles.title}>Set New Password</h1>
            <p style={styles.subtitle}>Enter a new password for your WODGoat account.</p>

            <form style={styles.form} onSubmit={handleSubmit}>
              <div>
                <label style={styles.label} htmlFor="wg-new-password">New password</label>
                <div style={styles.inputWrapper}>
                  <input
                    id="wg-new-password"
                    className="wg-input"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => { setNewPassword(e.target.value); setValidationError(''); }}
                    disabled={isSubmitting}
                    placeholder="Min. 8 chars, uppercase, lowercase, number"
                    style={{ ...styles.input, opacity: isSubmitting ? 0.6 : 1 }}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    style={styles.toggleBtn}
                    onClick={() => setShowPassword((v) => !v)}
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              <div>
                <label style={styles.label} htmlFor="wg-confirm-password">Confirm password</label>
                <div style={styles.inputWrapper}>
                  <input
                    id="wg-confirm-password"
                    className="wg-input"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); setValidationError(''); }}
                    disabled={isSubmitting}
                    placeholder="Repeat your new password"
                    style={{ ...styles.input, opacity: isSubmitting ? 0.6 : 1 }}
                    autoComplete="new-password"
                  />
                </div>
              </div>

              {validationError && (
                <p style={styles.validationError}>{validationError}</p>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  ...styles.submitBtn,
                  ...(isSubmitting ? styles.submitBtnDisabled : {}),
                }}
              >
                {isSubmitting && <span className="wg-btn-spinner" />}
                {isSubmitting ? 'Updating…' : 'Update Password'}
              </button>
            </form>
          </>
        )}

        {/* Success */}
        {pageStatus === 'success' && mode && (
          <>
            <span className="wg-status-icon" style={styles.statusIcon}>✅</span>
            <h2 style={styles.statusTitle}>{successMessages[mode]?.title}</h2>
            <p style={styles.statusBody}>{successMessages[mode]?.body}</p>
            <div style={styles.divider} />
            <p style={styles.hint}>You can close this page and return to the WODGoat app.</p>
          </>
        )}

        {/* Error */}
        {pageStatus === 'error' && (
          <>
            <span className="wg-status-icon" style={styles.statusIcon}>⚠️</span>
            <h2 style={{ ...styles.statusTitle, color: theme.colors.error }}>Something went wrong</h2>
            <p style={styles.statusBody}>{errorMessage}</p>
            <div style={styles.divider} />
            <p style={styles.hint}>Return to the WODGoat app and try again.</p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthAction;
